import React, { useCallback, useState } from 'react';
import { formatAndValidateTime } from '../../../utils/DateUtil.ts';
import { Group, Select, TextInput } from '@mantine/core';
import { uid } from '../../../hooks/parking-services/useParkingRateGroups.ts';
import {
  crossDayDurationMinutes,
  DAY_SHORT,
} from '../../../utils/ParkingRateTime.ts';
import {
  type DraftFeeItem,
  type MaxFeeRuleEntry,
} from '../../../utils/ParkingRateConverters.ts';

interface MaxFeeRuleListEditorProps {
  entries: MaxFeeRuleEntry[];
  onChange: (entries: MaxFeeRuleEntry[]) => void;
  isSpecific: boolean;
  /** regular: DAY_SHORT (['一'…'日']). specific: formatted date labels */
  dayLabels: string[];
  colorScheme: 'light' | 'dark';
  /** `${entry.id}-${feeIdx}` keys of fee items whose 規則 is still unset after 下一步 was pressed */
  invalidFeeItemKeys?: Set<string>;
}

const computeOverlapSlotIds = (entry: MaxFeeRuleEntry): Set<number> => {
  const ids = new Set<number>();
  entry.checkedDays.forEach((dayIdx) => {
    const slots = entry.daySlots[dayIdx] ?? [];
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        if (
          slots[i].startTime < slots[j].endTime &&
          slots[i].endTime > slots[j].startTime
        ) {
          ids.add(slots[i].id);
          ids.add(slots[j].id);
        }
      }
    }
  });
  return ids;
};

const MaxFeeRuleListEditor: React.FC<MaxFeeRuleListEditorProps> = ({
  entries,
  onChange,
  isSpecific,
  dayLabels,
  colorScheme,
  invalidFeeItemKeys,
}) => {
  const border =
    colorScheme === 'light' ? '1px solid #e5e7eb' : '1px solid #525252';
  const divider =
    colorScheme === 'light' ? '1px solid #f3f4f6' : '1px solid #404040';
  const headerBg = colorScheme === 'light' ? '#f9fafb' : '#262626';
  // 時段欄的日期/星期標籤寬度：特殊費率顯示日期（如 12/31）較寬
  const dayLabelW = isSpecific ? 52 : 22;

  const updateEntry = useCallback(
    (id: number, updater: (e: MaxFeeRuleEntry) => MaxFeeRuleEntry) =>
      onChange(entries.map((e) => (e.id === id ? updater(e) : e))),
    [entries, onChange],
  );

  const deleteEntry = useCallback(
    (id: number) => onChange(entries.filter((e) => e.id !== id)),
    [entries, onChange],
  );

  const [timeErrors, setTimeErrors] = useState<
    Record<number, { start?: boolean; end?: boolean }>
  >({});

  const [overlapErrorsMap, setOverlapErrorsMap] = useState<
    Record<number, Set<number>>
  >({});

  // 時段刪除鍵的 hover 狀態（平時灰、hover 才轉紅），以 slot.id 為 key
  const [hoverDelSlotId, setHoverDelSlotId] = useState<number | null>(null);
  // 費用規則刪除鍵的 hover 狀態，以 `${entry.id}-${feeIdx}` 為 key
  const [hoverDelFeeKey, setHoverDelFeeKey] = useState<string | null>(null);

  if (entries.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '48px 16px',
          color: '#9ca3af',
          fontSize: 12,
          lineHeight: 1.8,
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 10 }}>⏱</div>
        尚未設定任何最高收費規則
        <br />
        點擊右上角「+ 新增」建立最高收費規則
      </div>
    );
  }

  return (
    <div>
      {entries.map((entry) => {
        const overlapSlotIds = overlapErrorsMap[entry.id] ?? new Set<number>();
        const toggleDay = (dayIdx: number, checked: boolean) =>
          updateEntry(entry.id, (prev) => {
            if (checked) {
              const newCheckedDays = [...prev.checkedDays, dayIdx].sort(
                (a, b) => a - b,
              );
              const newDaySlots = {
                ...prev.daySlots,
                [dayIdx]: prev.daySlots[dayIdx] ?? [
                  { id: uid(), startTime: '00:00', endTime: '23:59' },
                ],
              };
              return {
                ...prev,
                checkedDays: newCheckedDays,
                daySlots: newDaySlots,
              };
            } else {
              const newCheckedDays = prev.checkedDays.filter(
                (d) => d !== dayIdx,
              );
              const { [dayIdx]: _removed, ...rest } = prev.daySlots;
              return { ...prev, checkedDays: newCheckedDays, daySlots: rest };
            }
          });

        const applyQuickSelect = (indices: number[]) =>
          updateEntry(entry.id, (prev) => {
            const allSelected = indices.every((d) =>
              prev.checkedDays.includes(d),
            );
            if (allSelected) {
              const newCheckedDays = prev.checkedDays.filter(
                (d) => !indices.includes(d),
              );
              const newDaySlots = { ...prev.daySlots };
              indices.forEach((d) => delete newDaySlots[d]);
              return {
                ...prev,
                checkedDays: newCheckedDays,
                daySlots: newDaySlots,
              };
            }
            const newCheckedDays = [
              ...new Set([...prev.checkedDays, ...indices]),
            ].sort((a, b) => a - b);
            const newDaySlots = { ...prev.daySlots };
            indices.forEach((dayIdx) => {
              if (!newDaySlots[dayIdx]) {
                newDaySlots[dayIdx] = [
                  { id: uid(), startTime: '00:00', endTime: '23:59' },
                ];
              }
            });
            return {
              ...prev,
              checkedDays: newCheckedDays,
              daySlots: newDaySlots,
            };
          });

        const toggleWeekday = () => applyQuickSelect([0, 1, 2, 3, 4]);
        const toggleHoliday = () => applyQuickSelect([5, 6]);
        const toggleAll = () => applyQuickSelect([0, 1, 2, 3, 4, 5, 6]);

        const removeTimeSlot = (dayIdx: number, slotIdx: number) =>
          updateEntry(entry.id, (prev) => ({
            ...prev,
            daySlots: {
              ...prev.daySlots,
              [dayIdx]: (prev.daySlots[dayIdx] ?? []).filter(
                (_, i) => i !== slotIdx,
              ),
            },
          }));

        const updateTimeSlot = (
          dayIdx: number,
          slotIdx: number,
          field: 'startTime' | 'endTime',
          value: string,
        ) =>
          updateEntry(entry.id, (prev) => {
            const slots = [...(prev.daySlots[dayIdx] ?? [])];
            slots[slotIdx] = { ...slots[slotIdx], [field]: value };
            return { ...prev, daySlots: { ...prev.daySlots, [dayIdx]: slots } };
          });

        const commitTime = (
          slotId: number,
          dayIdx: number,
          slotIdx: number,
          field: 'startTime' | 'endTime',
          raw: string,
        ) => {
          const errorKey = field === 'startTime' ? 'start' : 'end';
          const { formatted, valid } = formatAndValidateTime(raw);
          if (valid) {
            updateTimeSlot(dayIdx, slotIdx, field, formatted);
            setTimeErrors((prev) => {
              const next = { ...prev[slotId] };
              delete next[errorKey];
              return { ...prev, [slotId]: next };
            });
            const updatedSlots = (entry.daySlots[dayIdx] ?? []).map((s, i) =>
              i === slotIdx ? { ...s, [field]: formatted } : s,
            );
            setOverlapErrorsMap((prev) => ({
              ...prev,
              [entry.id]: computeOverlapSlotIds({
                ...entry,
                daySlots: { ...entry.daySlots, [dayIdx]: updatedSlots },
              }),
            }));
          } else {
            setTimeErrors((prev) => ({
              ...prev,
              [slotId]: { ...prev[slotId], [errorKey]: true },
            }));
          }
        };

        // 把「來源天」的完整時段清單複製到 checkedDays 中排在它之後的每一天。
        // 每個被複製的 slot 都配新的 uid()，避免 React key / overlap id 衝突。
        const applyDayBelow = (sourceDayIdx: number) => {
          const sourceSlots = entry.daySlots[sourceDayIdx] ?? [];
          const newDaySlots = { ...entry.daySlots };
          let pastSource = false;
          entry.checkedDays.forEach((dIdx) => {
            if (dIdx === sourceDayIdx) {
              pastSource = true;
            } else if (pastSource) {
              newDaySlots[dIdx] = sourceSlots.map((s) => ({
                id: uid(),
                startTime: s.startTime,
                endTime: s.endTime,
              }));
            }
          });
          updateEntry(entry.id, (prev) => ({ ...prev, daySlots: newDaySlots }));
          // 被覆蓋的天換了新 slot id，重算整個 entry 的 overlap（用同一份 newDaySlots
          // 確保 id 與實際 state 一致）。
          setOverlapErrorsMap((prev) => ({
            ...prev,
            [entry.id]: computeOverlapSlotIds({
              ...entry,
              daySlots: newDaySlots,
            }),
          }));
        };

        const addFeeItem = () =>
          updateEntry(entry.id, (prev) => ({
            ...prev,
            feeItems: [...prev.feeItems, { mode: '', fee: '', interval: '' }],
          }));

        const removeFeeItem = (feeIdx: number) =>
          updateEntry(entry.id, (prev) => ({
            ...prev,
            feeItems: prev.feeItems.filter((_, i) => i !== feeIdx),
          }));

        const updateFeeItem = (
          feeIdx: number,
          updater: (f: DraftFeeItem) => DraftFeeItem,
        ) =>
          updateEntry(entry.id, (prev) => ({
            ...prev,
            feeItems: prev.feeItems.map((f, i) =>
              i === feeIdx ? updater(f) : f,
            ),
          }));

        return (
          <div
            key={entry.id}
            style={{
              border,
              borderRadius: 10,
              marginBottom: 12,
              overflow: 'hidden',
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            {/* Row header */}
            <div
              style={{
                padding: '8px 12px',
                background: headerBg,
                borderBottom: divider,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}></div>
              <button
                onClick={() => deleteEntry(entry.id)}
                style={{
                  padding: '1px 7px',
                  borderRadius: 6,
                  border: '1px solid #fca5a5',
                  background: 'transparent',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                x
              </button>
            </div>

            {/* 3-column body */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '176px 265px 352px',
                minHeight: 80,
              }}
            >
              {/* ── Column 1: 星期 ── */}
              <div
                style={{
                  padding: '10px 12px',
                  borderRight: divider,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: '#9ca3af',
                    marginBottom: 10,
                    fontWeight: 600,
                  }}
                >
                  星期
                </div>
                {!isSpecific && (
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                    {[
                      { label: '平日', onToggle: toggleWeekday },
                      { label: '假日', onToggle: toggleHoliday },
                      { label: '全選', onToggle: toggleAll },
                    ].map(({ label, onToggle }) => (
                      <button
                        key={label}
                        onClick={onToggle}
                        style={{
                          flex: 1,
                          padding: '3px 0',
                          borderRadius: 5,
                          border: '1px solid #e5e7eb',
                          background: '#f9fafb',
                          color: '#6b7280',
                          cursor: 'pointer',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <div
                  style={{
                    display: isSpecific ? 'flex' : 'grid',
                    flexWrap: isSpecific ? 'wrap' : undefined,
                    gridTemplateColumns: isSpecific
                      ? undefined
                      : 'repeat(4, 1fr)',
                    gap: isSpecific ? '4px 8px' : 3,
                    maxHeight: isSpecific ? 110 : undefined,
                    overflowY: isSpecific ? 'auto' : undefined,
                  }}
                >
                  {dayLabels.map((label, dayIdx) => {
                    const checked = entry.checkedDays.includes(dayIdx);
                    return (
                      <label
                        key={dayIdx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          cursor: 'pointer',
                          fontSize: 12,
                          padding: '2px 4px',
                          borderRadius: 4,
                          background: checked ? '#dbeafe' : 'transparent',
                          color: checked ? '#1d4ed8' : undefined,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          userSelect: 'none',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleDay(dayIdx, e.target.checked)}
                          style={{
                            width: 12,
                            height: 12,
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* ── Column 2: 時段 ── */}
              <div
                style={{
                  padding: '10px 12px',
                  borderRight: divider,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: '#9ca3af',
                    marginBottom: 6,
                    fontWeight: 600,
                  }}
                >
                  時段
                </div>
                {entry.checkedDays.length === 0 ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#d1d5db',
                      fontStyle: 'italic',
                      width: 241,
                    }}
                  >
                    勾選星期後自動新增
                  </div>
                ) : (
                  entry.checkedDays.map((dayIdx) => {
                    const slots = entry.daySlots[dayIdx] ?? [];
                    const dayLabel = isSpecific
                      ? dayLabels[dayIdx].split('(')[0] // 只取日期（如 7/2），不含星期
                      : DAY_SHORT[dayIdx];
                    return (
                      <div
                        key={dayIdx}
                        style={{ marginBottom: 8, width: 'fit-content' }}
                      >
                        {slots.map((slot, slotIdx) => (
                          <Group
                            key={slot.id}
                            gap={4}
                            wrap="nowrap"
                            style={{
                              alignItems: 'center',
                              marginBottom: 4,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                color: '#6b7280',
                                width: dayLabelW,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {slotIdx === 0 ? `${dayLabel}：` : '　'}
                            </span>
                            <TextInput
                              value={slot.startTime}
                              onChange={(e) =>
                                updateTimeSlot(
                                  dayIdx,
                                  slotIdx,
                                  'startTime',
                                  e.target.value,
                                )
                              }
                              onBlur={(e) =>
                                commitTime(
                                  slot.id,
                                  dayIdx,
                                  slotIdx,
                                  'startTime',
                                  e.target.value,
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  commitTime(
                                    slot.id,
                                    dayIdx,
                                    slotIdx,
                                    'startTime',
                                    e.currentTarget.value,
                                  );
                              }}
                              error={
                                !!timeErrors[slot.id]?.start ||
                                overlapSlotIds.has(slot.id)
                              }
                              placeholder="HHMM"
                              size="xs"
                              style={{ width: 68 }}
                              styles={{ input: { textAlign: 'center' } }}
                            />
                            <span
                              style={{
                                paddingInline: 4,
                                fontSize: 11,
                                color: '#9ca3af',
                              }}
                            >
                              ~
                            </span>
                            <TextInput
                              value={slot.endTime}
                              onChange={(e) =>
                                updateTimeSlot(
                                  dayIdx,
                                  slotIdx,
                                  'endTime',
                                  e.target.value,
                                )
                              }
                              onBlur={(e) =>
                                commitTime(
                                  slot.id,
                                  dayIdx,
                                  slotIdx,
                                  'endTime',
                                  e.target.value,
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  commitTime(
                                    slot.id,
                                    dayIdx,
                                    slotIdx,
                                    'endTime',
                                    e.currentTarget.value,
                                  );
                              }}
                              error={
                                !!timeErrors[slot.id]?.end ||
                                overlapSlotIds.has(slot.id)
                              }
                              placeholder="HHMM"
                              size="xs"
                              style={{ width: 68 }}
                              styles={{ input: { textAlign: 'center' } }}
                            />
                            {slots.length > 1 ? (
                              <button
                                onClick={() => {
                                  removeTimeSlot(dayIdx, slotIdx);
                                  const remaining = (
                                    entry.daySlots[dayIdx] ?? []
                                  ).filter((_, i) => i !== slotIdx);
                                  setOverlapErrorsMap((prev) => ({
                                    ...prev,
                                    [entry.id]: computeOverlapSlotIds({
                                      ...entry,
                                      daySlots: {
                                        ...entry.daySlots,
                                        [dayIdx]: remaining,
                                      },
                                    }),
                                  }));
                                }}
                                onMouseEnter={() => setHoverDelSlotId(slot.id)}
                                onMouseLeave={() => setHoverDelSlotId(null)}
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  border: 'none',
                                  background:
                                    hoverDelSlotId === slot.id
                                      ? '#fef2f2'
                                      : 'transparent',
                                  color:
                                    hoverDelSlotId === slot.id
                                      ? '#ef4444'
                                      : '#9ca3af',
                                  cursor: 'pointer',
                                  fontSize: 14,
                                  lineHeight: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  marginLeft: 2,
                                  flexShrink: 0,
                                }}
                              >
                                ×
                              </button>
                            ) : (
                              // 無 × 時保留等寬佔位，確保每列寬度一致、
                              // 「套用至下方」跨天對齊
                              <span
                                aria-hidden
                                style={{
                                  width: 20,
                                  marginLeft: 2,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Group>
                        ))}
                        {(entry.daySlots[dayIdx] ?? []).some((s) =>
                          overlapSlotIds.has(s.id),
                        ) && (
                          <div
                            style={{
                              marginLeft: dayLabelW,
                              marginTop: 2,
                              fontSize: 11,
                              color: '#ef4444',
                            }}
                          >
                            時段重疊
                          </div>
                        )}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 8,
                            // 左扣（標籤寬 + gap4）對齊起始框左緣、右扣 26（× 欄：
                            // gap4 + marginLeft2 + 寬20）→ 右緣對齊結束時間框末端
                            marginLeft: dayLabelW + 4,
                            width: `calc(100% - ${dayLabelW + 30}px)`,
                            marginTop: 6,
                          }}
                        >
                          {entry.checkedDays[entry.checkedDays.length - 1] !==
                            dayIdx && (
                            <button
                              onClick={() => applyDayBelow(dayIdx)}
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: '#51e66e',
                                background: '#f0fdf4',
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                padding: '3px 10px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              ↓ 套用至下方
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* ── Column 3: 規則 ── */}
              <div style={{ padding: '10px 12px' }}>
                <div
                  style={{
                    fontSize: 11,
                    color: '#9ca3af',
                    marginBottom: 6,
                    fontWeight: 600,
                  }}
                >
                  規則
                </div>
                {entry.feeItems.map((item, feeIdx) => (
                  <div
                    key={feeIdx}
                    style={{
                      marginBottom: 8,
                      paddingBottom: 8,
                      borderBottom:
                        feeIdx < entry.feeItems.length - 1 ? divider : 'none',
                    }}
                  >
                    {/* mode selector row */}
                    <Group
                      style={{
                        gap: 12,
                        marginBottom: 5,
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Select
                        size={'xs'}
                        placeholder="請選擇"
                        value={item.mode || null}
                        error={
                          item.mode === '' &&
                          invalidFeeItemKeys?.has(`${entry.id}-${feeIdx}`)
                        }
                        data={[
                          { label: '單日最高', value: 'WHOLE_DAY' },
                          { label: '前 X 分鐘', value: 'FIRST_UNIT' },
                          { label: '每 X 分鐘', value: 'PER_UNIT' },
                        ]}
                        onChange={(value) =>
                          updateFeeItem(feeIdx, (f) => {
                            // 切到「前 X 分鐘」時，用目前勾選中最前面那天的時段
                            // 時長預帶入分鐘數（使用者之後仍可自行修改）。
                            if (value === 'FIRST_UNIT') {
                              const firstSlot =
                                entry.daySlots[entry.checkedDays[0]]?.[0];
                              const interval = firstSlot
                                ? String(
                                    crossDayDurationMinutes(
                                      firstSlot.startTime,
                                      firstSlot.endTime,
                                    ),
                                  )
                                : '';
                              return {
                                ...f,
                                mode: value ?? '',
                                fee: '',
                                interval,
                              };
                            }
                            return {
                              ...f,
                              mode: value ?? '',
                              fee: '',
                              interval: '',
                            };
                          })
                        }
                        style={{ width: 100 }}
                      />
                      <Group gap={4}>
                        <Group gap={4}>
                          {item.mode !== '' && item.mode !== 'WHOLE_DAY' && (
                            <>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: '#9ca3af',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.mode === 'FIRST_UNIT' ? '前' : '每'}
                              </span>
                              <TextInput
                                value={item.interval}
                                onChange={(e) =>
                                  updateFeeItem(feeIdx, (f) => ({
                                    ...f,
                                    interval: e.target.value,
                                  }))
                                }
                                placeholder="分鐘"
                                size="xs"
                                style={{ width: 52 }}
                              />
                              <span
                                style={{
                                  fontSize: 11,
                                  color: '#9ca3af',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                分鐘
                              </span>
                            </>
                          )}
                        </Group>
                        <Group gap={4}>
                          <span
                            style={{
                              fontSize: 11,
                              color: '#9ca3af',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            最高
                          </span>
                          <TextInput
                            value={item.fee}
                            onChange={(e) =>
                              updateFeeItem(feeIdx, (f) => ({
                                ...f,
                                fee: e.target.value,
                              }))
                            }
                            placeholder="金額"
                            size="xs"
                            style={{ flex: 1, width: 52 }}
                          />
                          <span
                            style={{
                              fontSize: 11,
                              color: '#9ca3af',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            元
                          </span>
                          {entry.feeItems.length > 1 && (
                            <button
                              onClick={() => removeFeeItem(feeIdx)}
                              onMouseEnter={() =>
                                setHoverDelFeeKey(`${entry.id}-${feeIdx}`)
                              }
                              onMouseLeave={() => setHoverDelFeeKey(null)}
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                border: 'none',
                                background:
                                  hoverDelFeeKey === `${entry.id}-${feeIdx}`
                                    ? '#fef2f2'
                                    : 'transparent',
                                color:
                                  hoverDelFeeKey === `${entry.id}-${feeIdx}`
                                    ? '#ef4444'
                                    : '#9ca3af',
                                cursor: 'pointer',
                                fontSize: 14,
                                lineHeight: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                marginLeft: 2,
                                flexShrink: 0,
                              }}
                            >
                              ×
                            </button>
                          )}
                        </Group>
                      </Group>
                    </Group>
                  </div>
                ))}
                <button
                  onClick={addFeeItem}
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: '#39bef3',
                    background: '#ecfeff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    padding: '3px 10px',
                  }}
                >
                  + 新增費用規則
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MaxFeeRuleListEditor;
